import { useState } from "react";
import { graphql, useMutation } from "react-relay";
import { Button, toast, Select } from "@olivahealth/oli-ui";
import { ModifySessionMutation } from "./__generated__/ModifySessionMutation.graphql";
import SessionSelector from "./SessionSelector";
import { SessionStatusEnum } from "./__generated__/SessionSelectorQuery.graphql";

const ModifySessionMutation = graphql`
  mutation ModifySessionMutation(
    $sessionId: ID!
    $status: SessionStatusEnum!
    $date: DateTime
  ) {
    adminUpdateSession(sessionId: $sessionId, status: $status, date: $date) {
      __typename
      ... on AdminSessionUpdated {
        success
        sessionId
      }
      ... on ForbiddenError {
        reason
      }
    }
  }
`;

const statusOptions = [
  { value: "ACTIVE", label: "Active" },
  { value: "AWAITING_PAYMENT", label: "Awaiting Payment" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "CANCELLED_LATE", label: "Cancelled Late" },
  { value: "COMPLETED", label: "Completed" },
  { value: "EMPLOYEE_NO_SHOW", label: "Employee No Show" },
  { value: "NO_SHOW", label: "No Show" },
  { value: "PAYMENT_EXPIRED", label: "Payment Expired" },
  { value: "PROFESSIONAL_NO_SHOW", label: "Professional No Show" },
  { value: "SCHEDULED", label: "Scheduled" },
];

export default function ModifySession(): JSX.Element {
  const [selectedSessionId, setSelectedSessionId] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<SessionStatusEnum | "">(
    "",
  );
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [mutate, isUpdating] = useMutation<ModifySessionMutation>(
    ModifySessionMutation,
  );

  const handleUpdateSession = () => {
    if (!selectedSessionId) return;

    const variables: any = {
      sessionId: selectedSessionId,
    };

    if (selectedStatus) {
      variables.status = selectedStatus;
    }

    if (selectedDate) {
      const date = new Date(selectedDate);
      variables.date = date.toISOString();
    }

    if (!selectedStatus && !selectedDate) {
      toast({
        body: "Please select a status or date to update",
        variant: "error",
      });
      return;
    }

    mutate({
      variables,
      onCompleted: (response) => {
        if (response.adminUpdateSession.__typename === "AdminSessionUpdated") {
          toast({
            body: "Session has been updated successfully",
            variant: "success",
          });
          setSelectedSessionId("");
          setSelectedStatus("");
          setSelectedDate("");
        } else if (
          response.adminUpdateSession.__typename === "ForbiddenError"
        ) {
          toast({ body: response.adminUpdateSession.reason, variant: "error" });
        }
      },
      onError: (error) => {
        toast({ body: error.message, variant: "error" });
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold">Modify Session</h3>
      <div className="flex-1">
        <SessionSelector
          onSessionSelected={setSelectedSessionId}
          placeholder="Search by sessionId, userId, professionalId or organisationId..."
        />
      </div>

      {selectedSessionId && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <Select
              id="session-status"
              items={statusOptions}
              onChange={(option) =>
                setSelectedStatus((option?.value as SessionStatusEnum) || "")
              }
              preSelectedItem={
                statusOptions.find(
                  (option) => option.value === selectedStatus,
                ) || undefined
              }
              placeholder="Select status"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date and Time
            </label>
            <input
              type="datetime-local"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>

          <Button
            disabled={(!selectedStatus && !selectedDate) || isUpdating}
            onClick={handleUpdateSession}
            variant="primary"
          >
            {isUpdating ? "Updating..." : "Update Session"}
          </Button>
        </div>
      )}
    </div>
  );
}
